import { LitElement, html, css } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { db } from '../../database/schema.js'
import { featureStorage } from '../../storage/feature-storage.js'
import type { Route } from '../../schemas/route.js'
import type { Feature } from '../../schemas/feature.js'

@customElement('plan-feature-import')
export class PlanFeatureImport extends LitElement {
  static override styles = css`
    :host {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 3000;
      pointer-events: none;
    }

    .backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.4);
      pointer-events: auto;
    }

    .panel {
      position: relative;
      background: rgba(30, 41, 59, 0.97);
      backdrop-filter: blur(12px);
      border-radius: 16px 16px 0 0;
      padding: 16px;
      max-height: 60vh;
      overflow-y: auto;
      pointer-events: auto;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .panel-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .panel-title {
      font-size: 16px;
      font-weight: 600;
      color: white;
    }

    .close-btn {
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.7);
      cursor: pointer;
      font-family: 'Material Symbols Outlined';
      font-size: 24px;
      padding: 4px;
      line-height: 1;
    }

    .close-btn:hover {
      color: white;
    }

    .route-item {
      background: rgba(255, 255, 255, 0.06);
      border-radius: 10px;
      overflow: hidden;
    }

    .route-header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 12px;
      cursor: pointer;
      color: white;
      font-size: 14px;
      font-weight: 500;
      border: none;
      background: none;
      width: 100%;
      text-align: left;
    }

    .route-header:hover {
      background: rgba(255, 255, 255, 0.06);
    }

    .route-header .expand-icon {
      font-family: 'Material Symbols Outlined';
      font-size: 20px;
      color: rgba(255, 255, 255, 0.5);
      transition: transform 0.2s ease;
    }

    .route-header .expand-icon.expanded {
      transform: rotate(90deg);
    }

    .route-name {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .feature-count {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.4);
    }

    .feature-list {
      padding: 0 12px 10px;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .feature-row {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 0;
    }

    .feature-row input[type='checkbox'] {
      accent-color: #3b82f6;
      width: 16px;
      height: 16px;
      cursor: pointer;
    }

    .feature-name {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.85);
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .feature-type {
      font-size: 11px;
      color: rgba(255, 255, 255, 0.4);
      text-transform: uppercase;
    }

    .import-btn {
      background: #3b82f6;
      color: white;
      border: none;
      border-radius: 10px;
      padding: 10px 16px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.15s ease;
    }

    .import-btn:hover {
      background: #2563eb;
    }

    .import-btn:disabled {
      background: rgba(59, 130, 246, 0.3);
      color: rgba(255, 255, 255, 0.4);
      cursor: not-allowed;
    }

    .empty-state {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.4);
      text-align: center;
      padding: 24px 0;
    }

    .loading {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.5);
      text-align: center;
      padding: 24px 0;
    }
  `

  @property({ type: String }) entityType = 'plan'
  @property({ type: String }) entityId = ''
  @property({ type: Boolean }) open = false

  @state() private routes: Route[] = []
  @state() private expandedRouteId: string | null = null
  @state() private routeFeatures: Map<string, Feature[]> = new Map()
  @state() private selectedFeatureIds: Set<string> = new Set()
  @state() private loading = false
  @state() private importing = false

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('open') && this.open) {
      void this.loadRoutes()
    }
  }

  private async loadRoutes(): Promise<void> {
    this.loading = true
    try {
      const allRoutes = await db.routes.toArray()
      this.routes = allRoutes.filter(r => r.id !== this.entityId)
    } finally {
      this.loading = false
    }
  }

  private async toggleRoute(routeId: string): Promise<void> {
    if (this.expandedRouteId === routeId) {
      this.expandedRouteId = null
      return
    }
    this.expandedRouteId = routeId
    if (!this.routeFeatures.has(routeId)) {
      const features = await featureStorage.getFeaturesForEntity('route', routeId)
      this.routeFeatures = new Map(this.routeFeatures).set(routeId, features)
    }
  }

  private toggleFeature(featureId: string): void {
    const next = new Set(this.selectedFeatureIds)
    if (next.has(featureId)) {
      next.delete(featureId)
    } else {
      next.add(featureId)
    }
    this.selectedFeatureIds = next
  }

  private async importSelected(): Promise<void> {
    if (this.selectedFeatureIds.size === 0 || this.importing) return
    this.importing = true
    try {
      const imported: Feature[] = []
      for (const [, features] of this.routeFeatures) {
        for (const feature of features) {
          if (!this.selectedFeatureIds.has(feature.id)) continue
          const tags = [...feature.tags, 'imported'].filter((t, i, a) => a.indexOf(t) === i)
          const result = await featureStorage.createFeature(
            {
              name: feature.name,
              description: feature.description,
              type: feature.type,
              geometry: structuredClone(feature.geometry),
              properties: feature.properties ? structuredClone(feature.properties) : undefined,
              style: feature.style ? structuredClone(feature.style) : undefined,
              label: feature.label ? structuredClone(feature.label) : undefined,
              tags,
              isVisible: feature.isVisible,
              waypointType: feature.waypointType,
              createdBy: feature.createdBy,
            },
            { entityType: this.entityType, entityId: this.entityId }
          )
          if (result.success && result.data) {
            imported.push(result.data)
          }
        }
      }
      this.dispatchEvent(
        new CustomEvent('features-imported', { detail: { features: imported }, bubbles: true })
      )
      this.close()
    } finally {
      this.importing = false
    }
  }

  private close(): void {
    this.selectedFeatureIds = new Set()
    this.expandedRouteId = null
    this.dispatchEvent(new CustomEvent('close-import', { bubbles: true }))
  }

  override render(): unknown {
    if (!this.open) return html``

    return html`
      <div class="backdrop" @click=${this.close}></div>
      <div class="panel">
        <div class="panel-header">
          <span class="panel-title">Import Features</span>
          <button class="close-btn" @click=${this.close}>close</button>
        </div>

        ${this.loading
          ? html`<div class="loading">Loading routes...</div>`
          : this.routes.length === 0
            ? html`<div class="empty-state">No other routes available</div>`
            : this.routes.map(route => this.renderRoute(route))}

        <button
          class="import-btn"
          ?disabled=${this.selectedFeatureIds.size === 0 || this.importing}
          @click=${this.importSelected}
        >
          ${this.importing ? 'Importing...' : `Import Selected (${this.selectedFeatureIds.size})`}
        </button>
      </div>
    `
  }

  private renderRoute(route: Route): unknown {
    const isExpanded = this.expandedRouteId === route.id
    const features = this.routeFeatures.get(route.id) ?? []

    return html`
      <div class="route-item">
        <button class="route-header" @click=${() => this.toggleRoute(route.id)}>
          <span class="expand-icon ${isExpanded ? 'expanded' : ''}">chevron_right</span>
          <span class="route-name">${route.name}</span>
          ${isExpanded ? html`<span class="feature-count">${features.length} features</span>` : ''}
        </button>
        ${isExpanded
          ? html`
              <div class="feature-list">
                ${features.length === 0
                  ? html`<div class="empty-state">No features in this route</div>`
                  : features.map(
                      f => html`
                        <label class="feature-row">
                          <input
                            type="checkbox"
                            .checked=${this.selectedFeatureIds.has(f.id)}
                            @change=${() => this.toggleFeature(f.id)}
                          />
                          <span class="feature-name">${f.name}</span>
                          <span class="feature-type">${f.type}</span>
                        </label>
                      `
                    )}
              </div>
            `
          : ''}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'plan-feature-import': PlanFeatureImport
  }
}
